/**
 * Entrega de documentos al cliente en LOTE: N chasis a una misma persona.
 *
 * La entrega era de un chasis a la vez, así que quien compraba cinco motores
 * obligaba al cajero a repetir el flujo cinco veces (cinco veces los datos de
 * quien recibe, cinco recibos) y nada relacionaba esas entregas entre sí.
 * Aquí vive la lógica del lote —armarlo, validarlo y resumirlo— fuera de la
 * pantalla, que es lo que permite probarla: el riesgo del lote no está en los
 * checkboxes sino en dejar pasar un chasis que no se podía entregar.
 *
 * El lote es una lista de candidatos de /api/entrega_candidatos con los tipos
 * que el cajero marcó:
 *
 *   [{ chasis, cliente:{cedula,nombres,apellidos}, documentos:[…],
 *      entregables:["placa"], bloqueo:"", tipos:["placa"] }]
 *
 * El servidor revalida todo (/api/entrega_procesar, todo o nada); estas
 * validaciones son para no mandar un lote que ya se sabe que va a fallar.
 */
import { codigoSucursal, labelTipo, nombreSucursal } from "lib/documentosvehiculo";

/**
 * Clave para comparar dos documentos de identidad. Cédula, RNC y pasaporte
 * comparten la columna `cedula`, así que se quitan los separadores SIN
 * reducir a dígitos: un pasaporte es alfanumérico y perdería su identidad.
 */
export const claveDocumento = (valor) =>
  String(valor || "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase();

/** Nombre completo del cliente de un candidato ("" si no se conoce). */
export const nombreCliente = (cliente) =>
  [cliente?.nombres, cliente?.apellidos]
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .join(" ");

/**
 * Candidatos agrupados por documento de identidad del comprador, de mayor a
 * menor cantidad de chasis: es el atajo de la pantalla ("este cliente tiene 5
 * chasis con documentos pendientes aquí"). Los chasis sin cliente conocido
 * quedan fuera — no se pueden agrupar con nadie.
 */
export const agruparPorCliente = (candidatos) => {
  const grupos = new Map();
  (candidatos || []).forEach((c) => {
    const clave = claveDocumento(c?.cliente?.cedula);
    if (!clave) return;
    if (!grupos.has(clave)) grupos.set(clave, { clave, cliente: c.cliente, chasis: [] });
    grupos.get(clave).chasis.push(c);
  });
  return [...grupos.values()].sort((a, b) => b.chasis.length - a.chasis.length);
};

/** Candidatos cuyo comprador es el del documento dado. */
export const chasisDeCliente = (candidatos, documento) => {
  const clave = claveDocumento(documento);
  if (!clave) return [];
  return (candidatos || []).filter((c) => claveDocumento(c?.cliente?.cedula) === clave);
};

/**
 * Lote + candidato, con todos sus documentos entregables ya marcados (que es
 * lo que el cajero quiere el 90% de las veces). Un chasis que ya está en el
 * lote no se duplica: el servidor rechaza el lote con chasis repetidos.
 */
export const agregarChasis = (lote, candidato) => {
  if (!candidato?.chasis) return lote;
  if ((lote || []).some((l) => l.chasis === candidato.chasis)) return lote;
  return [...(lote || []), { ...candidato, tipos: [...(candidato.entregables || [])] }];
};

/** Lote sin ese chasis. */
export const quitarChasis = (lote, chasis) => (lote || []).filter((l) => l.chasis !== chasis);

/**
 * Marca o desmarca un tipo de documento de un chasis del lote. Los marcados
 * se ordenan según `entregables` (el orden del catálogo) para que el recibo
 * no salga en el orden de los clics.
 */
export const alternarTipo = (lote, chasis, tipo) =>
  (lote || []).map((l) => {
    if (l.chasis !== chasis) return l;
    const marcados = new Set(l.tipos || []);
    if (marcados.has(tipo)) marcados.delete(tipo);
    else marcados.add(tipo);
    const orden = l.entregables || [];
    return {
      ...l,
      tipos: [
        ...orden.filter((t) => marcados.has(t)),
        ...[...marcados].filter((t) => !orden.includes(t)),
      ],
    };
  });

/** { chasis, documentos } del lote (los chasis sin tipos marcados no cuentan). */
export const totalesLote = (lote) => {
  const conDocs = (lote || []).filter((l) => (l.tipos || []).length > 0);
  return {
    chasis: conDocs.length,
    documentos: conDocs.reduce((a, l) => a + l.tipos.length, 0),
  };
};

/** "3 documento(s) de 2 chasis" para el botón y la confirmación. */
export const resumenLote = (lote) => {
  const { chasis, documentos } = totalesLote(lote);
  return `${documentos} documento(s) de ${chasis} chasis`;
};

/** Payload `items` de /api/entrega_procesar. */
export const itemsLote = (lote) =>
  (lote || [])
    .filter((l) => (l.tipos || []).length > 0)
    .map((l) => ({ chasis: l.chasis, tipos: [...l.tipos] }));

/** Sucursal donde está un documento del candidato, para explicar un rechazo. */
const sucursalDeTipo = (candidato, tipo) =>
  ((candidato?.documentos || []).find((d) => d.tipo === tipo) || {}).sucursal || "";

/**
 * Primer motivo por el que el lote no se puede mandar, o "" si está listo.
 * Mismo orden que el escritorio: chasis (recibida → placa/endoso) →
 * documentos → receptor → sucursal.
 */
export const validarLote = ({ lote, receptor, fecha, sucursalEntrega }) => {
  const conDocs = (lote || []).filter((l) => (l.tipos || []).length > 0);
  if (!(lote || []).length) return "Agregue al menos un chasis al lote";

  const bloqueado = (lote || []).find((l) => l.bloqueo);
  if (bloqueado) return `${bloqueado.bloqueo} (chasis ${bloqueado.chasis})`;

  if (!conDocs.length) return "Marque al menos un documento a entregar";
  const vacio = (lote || []).find((l) => !(l.tipos || []).length);
  if (vacio)
    return `El chasis ${vacio.chasis} no tiene documentos marcados: márquelos o quítelo del lote`;

  const { nombres, apellidos, cedula } = receptor || {};
  if (![nombres, apellidos, cedula, fecha].every((v) => String(v || "").trim()))
    return "Complete nombres, apellidos, cédula y fecha de quien recibe";

  if (!sucursalEntrega) return "Seleccione la sucursal de entrega";

  // Los documentos se entregan donde están recibidos: otra sucursal dejaría
  // el blob inconsistente (y el servidor lo rechaza igual).
  const destino = codigoSucursal(sucursalEntrega);
  for (let i = 0; i < conDocs.length; i += 1) {
    const l = conDocs[i];
    const fuera = l.tipos.find((t) => codigoSucursal(sucursalDeTipo(l, t)) !== destino);
    if (fuera) {
      const donde = sucursalDeTipo(l, fuera);
      return `El documento ${labelTipo(fuera)} del chasis ${l.chasis} está en la sucursal ${
        nombreSucursal(donde) || donde || "desconocida"
      }`;
    }
  }
  return "";
};

/** Texto del estado de un documento en la tabla del chasis. */
export const textoEstadoDocumento = (doc) => {
  if (!doc) return "";
  if (doc.estado === "no recibido") return "No recibido";
  if (doc.estado === "entregado") return "Entregado";
  if (doc.estado === "otra sucursal")
    return `En sucursal ${nombreSucursal(doc.sucursal) || doc.sucursal || "desconocida"}`;
  return doc.entransito ? "Disponible (hay un envío en tránsito)" : "Disponible";
};

/** Filas del recibo: una por chasis, en el orden en que se armó el lote. */
export const filasRecibo = (lote) =>
  itemsLote(lote).map((it) => {
    const candidato = (lote || []).find((l) => l.chasis === it.chasis) || {};
    return {
      chasis: it.chasis,
      vehiculo: [candidato.marca, candidato.modelo, candidato.colore]
        .map((x) => String(x || "").trim())
        .filter(Boolean)
        .join(" "),
      placa: candidato.placa || "",
      documentos: it.tipos.map(labelTipo).join(", "),
    };
  });
